import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { getCookies } from '@helpers/cookies';
import noop from '@helpers/noop';
import { ISingleBaseResponse } from '@interfaces/IBaseResponse';
import { IProfile } from '@interfaces/IProfile';
import { getMeProfileFromAPI } from '@libraries/api/login/login.api';
import { AxiosResponse } from 'axios';
import { useSetRecoilState } from 'recoil';
import { appMenu } from 'store/Common';

interface IUseAuth {
  isAuth: boolean;
  isGuest: boolean;
  profile: IProfile | null;
  token: string;
  reFetch: () => void;
  resetProfile: () => void;
}

const AuthContext = createContext<IUseAuth>({
  isAuth: false,
  isGuest: true,
  token: '',
  profile: null,
  reFetch: noop,
  resetProfile: noop,
});

type TAuthProvider = {
  children: ReactNode;
  token: string;
  initialProfile?: IProfile | null;
};

export const AuthProvider: React.FC<TAuthProvider> = ({
  children,
  token,
  initialProfile,
}) => {
  const [profile, setProfile] = useState<any | null>(initialProfile || null);
  const isGuest = getCookies('isGuest') === 'true' ? true : false;
  const setMenus = useSetRecoilState(appMenu);

  const getProfile = async () => {
    try {
      const response: AxiosResponse<ISingleBaseResponse<IProfile>> =
        await getMeProfileFromAPI();
      if (response.status === 200) {
        setProfile(response.data.data);
        setMenus(response?.data?.data?.role?.menus);
      }
    } catch (error) {
      resetProfile();
    }
  };

  const resetProfile = () => setProfile(null);

  useEffect(() => {
    token && !isGuest && getProfile();
  }, [token, isGuest]);

  const value = {
    isAuth: !!token,
    isGuest,
    profile: profile || initialProfile,
    reFetch: noop,
    token,
    resetProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): IUseAuth => useContext(AuthContext);
